import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Classes from '../Classes';
import StringFormatter from '../StringFormatter';
import logger from '../../logger';
import Utils from '../Utils';
import Stand from './Stand';
import { FavouriteConstants, MaxMobileScreenWidth, MixpanelEventName } from '../Constants';
import Image from './Image';
import PpsList from './PpsList';
import DateComponent from './DateComponent';
import Favourite from '../../participant-directory/Favourite';
import Tags from '../dtmTags';
import TranslationService from '../../translations';
import FeatureLabels from './FeatureLabels';
import Networking from '../../participant-directory/Networking';
import ChatButton from './ChatButton';
import { OPEN_CHAT_WINDOW } from '../chatAction';
import ParticipantContact from '../../participant-details/ParticipantContact';
import MixPanelHelper from '../MixPanelHelper';

const isParticipantInShowPlan = (participationId, showPlan) => showPlan && showPlan.participationIds
  && showPlan.participationIds.includes(participationId);

const isParticipantInNetworkFollowees = (userId, networkFollowees) => networkFollowees
    && networkFollowees.findIndex((networkFollowee) => networkFollowee.followeeId === userId) >= 0;

const alertInvalidOrganisation = (data, baseApiUrl, mode) => {
  if (!Utils.isRxAdmin(mode)) {
    const errorMessage = `Organization detail is invalid for the participant ${data.id}`;
    logger.logWarn(errorMessage, errorMessage, baseApiUrl);
  }
  return {};
};
const handleChatClick = (userId) => {
  if (window && window.chatterUiDispatcher) {
    window.chatterUiDispatcher.dispatch({ type: OPEN_CHAT_WINDOW, payload: userId });
  }
};
const isSameUser = (userId, loggedInUserId) => userId === loggedInUserId;

const handleClick = (url, event, eventName, participantId, interactedElement) => {
  MixPanelHelper.trackInteractedEvents(eventName, participantId, interactedElement);
};

const getexhibitingOrganisation = (data, baseApiUrl, context) => data.exhibitingOrganisation
  || alertInvalidOrganisation(data, baseApiUrl, context.mode);

const getexhibitingOrganisationId = (
  data,
  context,
  exhibitingOrganisation
) => (Utils.isRxAdmin(context.mode) ? data.exhibitingOrganisationId : exhibitingOrganisation.organisationId);

const getDisplayName = (
  context,
  data,
  exhibitingOrganisation
) => (Utils.isRxAdmin(context.mode) ? data.displayName : exhibitingOrganisation.displayName);

const getMiddleName = (data) => (data.middleName ? `${data.middleName} ` : data.middleName);

const getDesktopImageUrl = (data, assets) => (data.desktopUrl ? `${data.desktopUrl}` : assets.defaultParticipantPhotoUrl);

const getMobileImageUrl = (data, assets) => (data.mobileUrl ? `${data.mobileUrl}` : assets.defaultParticipantPhotoMobileUrl);

const checkIfFeatureFilterIsEmpty = (data) => data.featureFilters && data.featureFilters.length !== 0;

const getFeatureFiltersWithColourCode = (
  data,
  isFeatureFiltersEmpty,
  featureFilterColourCodesData
) => (isFeatureFiltersEmpty ? data.featureFilters.map((filter) => ({
  ...filter,
  colourCode: featureFilterColourCodesData && Object.hasOwn(featureFilterColourCodesData, filter.featureFilterId)
    ? featureFilterColourCodesData[filter.featureFilterId] : null
})) : []);

const checkIfNetworkingEnabled = (isNetworkParticipant, userId, loggedInUserId) => isNetworkParticipant && !isSameUser(userId, loggedInUserId);

const checkIfChatContainerEnabled = (context, userId, loggedInUserId) => context.isChatEnabled && !isSameUser(userId, loggedInUserId);

const featureLabelsSection = (isFeatureFiltersEmpty, featureFilterWithColourCode) => (isFeatureFiltersEmpty ? (
  <div>
    <FeatureLabels featureFilters={featureFilterWithColourCode} />
  </div>
) : null);

const exhibitingOrganisationStandsSection = (exhibitingOrganisation) => {
  const exhibitingOrganisationStands = (exhibitingOrganisation.stands && exhibitingOrganisation.stands.length) ? (
    <span>
      <Stand data={exhibitingOrganisation.stands} />
    </span>
  ) : null;
  return exhibitingOrganisationStands;
};

const participantContactSection = (data, contactTranslation, contactDetails, participantId) => {
  const particpantContact = (!data.hideContactDetailsForEventEdition && data.showContactInformation
    && (
    <ParticipantContact
      translations={contactTranslation}
      className={Classes.homePage.participantItem.participantContactDetails}
      data={contactDetails}
      showAllSocialMedia={false}
      tags={Tags.participantDirectory}
      isEditMode={false}
      hideContactDetailsAdmin={data.hideContactDetailsForEventEdition}
      participantId={participantId}
    />
    ));
  return particpantContact;
};

const favouriteIconSection = (
  data,
  translations,
  participationId,
  displayName,
  isAnonymousRegistrationEnabled,
  isInNetworkFollowees,
  showPlanningEnabled,
  isInShowPlan,
  desktopImageUrl,
  mobileImageUrl,
  participantId,
  enableNetworking,
  userId,
  updateFolloweeList,
  digitalPortfolioName
) => (
  <span className={`${Classes.favouriteIconContainer}`}>
    <Favourite
      participationId={participationId}
      displayName={displayName}
      jobTitle={data.jobTitle}
      firstName={data.firstName}
      middleName={data.middleName}
      lastName={data.lastName}
      exhibitingOrganisationId={data.exhibitingOrganisationId}
      photoKey={data.photoKey}
      initialState={isAnonymousRegistrationEnabled ? isInNetworkFollowees : isInShowPlan}
      triggeringComponent={FavouriteConstants.sourceDirectory}
      dtmData={Tags.participantDirectory}
      showPlanningEnabled={showPlanningEnabled}
      translations={isAnonymousRegistrationEnabled
        ? translations.directory.networking : translations.directory.favourite}
      desktopImageUrl={desktopImageUrl}
      mobileImageUrl={mobileImageUrl}
      participantId={participantId}
      enableNetworking={enableNetworking}
      userId={userId}
      updateFolloweeList={updateFolloweeList}
      digitalPortfolioName={digitalPortfolioName}
      isDirectoryView
      isAnonymousRegistrationEnabled={isAnonymousRegistrationEnabled}
    />
  </span>
);

const networkingIconSection = (
  enableNetworking,
  isAnonymousRegistrationEnabled,
  translations,
  baseApiUrl,
  participationId,
  isInNetworkFollowees,
  digitalPortfolioId,
  digitalPortfolioName,
  userId,
  context,
  updateFolloweeList
) => {
  const networkIcon = enableNetworking && !isAnonymousRegistrationEnabled ? (
    <span className={`${Classes.networkIconContainer}`}>
      <Networking
        baseApiUrl={baseApiUrl}
        participationId={participationId}
        initialState={isInNetworkFollowees}
        dtmData={Tags.participantDirectory}
        translations={translations.directory.networking}
        digitalPortfolioId={digitalPortfolioId}
        digitalPortfolioName={digitalPortfolioName}
        userId={userId}
        updateFolloweeList={updateFolloweeList}
        locale={context.interfaceLocale}
        isDirectoryView
      />
    </span>
  ) : null;
  return networkIcon;
};

const participantItemDescriptionSection = (data, translations, url, participantId) => (data.description ? (
  <div className={classnames(Classes.themeColor, Classes.homePage.participantItem.aboutMeTitle)}>
    {translations.directory.aboutMe}
    <a
      href={url}
      onClick={(event) => handleClick(url, event, MixpanelEventName.ParticipantInteracted, participantId, 'Description')}
      data-dtm-attributes={Tags.participantList.description}
    >
      <div className={Classes.homePage.participantItem.aboutMeTextSection}>
        {data.description}
      </div>
    </a>
  </div>
)
  : null);

const ParticipantItem = ({
  data, navigationUrl = '', dtmTag, context, companyDetailUrl = '',
  assets, baseApiUrl, showPlanningEnabled, showPlan, digitalPortfolioId, digitalPortfolioName,
  networkFollowees, updateFolloweeList, isNetworkParticipant, loggedInUserId, isAnonymousRegistrationEnabled
}) => {
  try {
    const { homePage } = Classes;
    const { participantId, userId } = data;
    const participationId = data.id;
    const attribute = { targetParticipationId: participationId };
    const url = Utils.generateParticipantURL(navigationUrl, participationId, context.interfaceLocale);
    const exhibitingOrganisation = getexhibitingOrganisation(data, baseApiUrl, context);
    const exhibitingOrganisationId = getexhibitingOrganisationId(data, context, exhibitingOrganisation);
    const displayName = getDisplayName(context, data, exhibitingOrganisation);
    const companyLink = StringFormatter.format(companyDetailUrl, exhibitingOrganisationId);
    const middleName = getMiddleName(data);
    const desktopImageUrl = getDesktopImageUrl(data, assets);
    const mobileImageUrl = getMobileImageUrl(data, assets);
    const maxWidth = `(max-width: ${MaxMobileScreenWidth}px)`;
    const createdDateInUtc = data.createdDateTime;
    const lastUpdatedDateInUtc = data.lastUpdatedDateTime;
    const translations = TranslationService.getTranslations(context.interfaceLocale);
    const contactTranslation = { ...translations.profile.contact, socialMedia: translations.socialMedia };
    const isFeatureFiltersEmpty = checkIfFeatureFilterIsEmpty(data);
    const featureFilterColourCodesData = Utils.getFromSessionStorage('eventEditionFeatureFilters');
    const featureFilterWithColourCode = getFeatureFiltersWithColourCode(data, isFeatureFiltersEmpty, featureFilterColourCodesData);
    const isInShowPlan = isParticipantInShowPlan(participationId, showPlan);
    const isInNetworkFollowees = isParticipantInNetworkFollowees(userId, networkFollowees);
    const enableNetworking = checkIfNetworkingEnabled(
      isNetworkParticipant,
      userId,
      loggedInUserId
    );
    const isEnableChatContainer = checkIfChatContainerEnabled(context, userId, loggedInUserId);
    const contactDetails = {
      email: data.email,
      phone: data.phone,
      showContactInformation: data.showContactInformation
    };

    return (
      <li className={`${homePage.participantItem.root} ${Classes.clearFix}`}>
        <div className={homePage.participantItem.participantWrapper}>
          <div className={homePage.participantItem.thumbnail}>
            <div>
              <a
                href={url}
                onClick={(event) => handleClick(url, event, MixpanelEventName.ParticipantInteracted, participantId, 'Image')}
              >
                <Image
                  mobileImageUrl={mobileImageUrl}
                  desktopImageUrl={desktopImageUrl}
                  maxWidth={maxWidth}
                  dtmTag={dtmTag.image}
                  dataDtmAttributes={JSON.stringify(attribute)}
                  alt={data.firstName}
                />
              </a>
              {featureLabelsSection(isFeatureFiltersEmpty, featureFilterWithColourCode)}
            </div>
          </div>
          <div className={homePage.participantItem.participantDetailsIconContactContainer}>
            <div className={homePage.participantItem.participantDetailsIconContainer}>
              <div className={homePage.participantItem.details}>
                <a
                  className={homePage.participantItem.name}
                  href={url}
                  onClick={(event) => handleClick(url, event, MixpanelEventName.ParticipantInteracted, participantId, 'Participant Title')}
                  data-dtm={dtmTag.participantItem}
                >
                  <h3>
                    {data.firstName}
                    {' '}
                    {middleName}
                    {data.lastName}
                  </h3>
                </a>
                {data.jobTitle && <div className={homePage.participantItem.jobTitle}>{data.jobTitle}</div>}

                <div className={homePage.participantItem.organization}>
                  <span>
                    <a
                      href={companyLink}
                      onClick={(event) => handleClick(companyLink, event, MixpanelEventName.ParticipantInteracted, participantId, 'Company Title')}
                      data-dtm={dtmTag.company}
                    >
                      {displayName}
                    </a>
                  </span>
                  {exhibitingOrganisationStandsSection(exhibitingOrganisation)}
                </div>
                <div className={homePage.participantItem.country}>
                  {exhibitingOrganisation.country}
                </div>

              </div>
              <div className={`${Classes.iconContainer}`}>
                {favouriteIconSection(
                  data,
                  translations,
                  participationId,
                  displayName,
                  isAnonymousRegistrationEnabled,
                  isInNetworkFollowees,
                  showPlanningEnabled,
                  isInShowPlan,
                  desktopImageUrl,
                  mobileImageUrl,
                  participantId,
                  enableNetworking,
                  userId,
                  updateFolloweeList,
                  digitalPortfolioName
                )}
                {networkingIconSection(
                  enableNetworking,
                  isAnonymousRegistrationEnabled,
                  translations,
                  baseApiUrl,
                  participationId,
                  isInNetworkFollowees,
                  digitalPortfolioId,
                  digitalPortfolioName,
                  userId,
                  context,
                  updateFolloweeList
                )}
                {isEnableChatContainer
                  ? (
                    <span className={`${Classes.chatElement.chatIconContainer}`}>
                      <ChatButton
                        text={contactTranslation.chat}
                        handleClick={() => handleChatClick(userId)}
                        dtmTagName={Tags.participantDetails.sendMessage}
                        dtmAttributes={JSON.stringify({
                          targetParticipationId: participationId
                        })}
                        iconClass={`${Classes.homePage.participantProfile.contact.chat}`}
                        className={`${Classes.chatElement.ChatParticipantDiv} ${Classes.primaryButton}  ${Classes.borderRadius}`}
                        isDirectoryView
                      />
                    </span>
                  ) : null}
              </div>
            </div>
            {participantContactSection(data, contactTranslation, contactDetails, participantId)}
          </div>
        </div>
        {participantItemDescriptionSection(data, translations, url, participantId)}
        <DateComponent lastUpdatedDateInUtc={lastUpdatedDateInUtc} createdDateInUtc={createdDateInUtc} context={context} />
        <PpsList
          participantPpsList={data.ppsFilterData}
          dtmTag={Tags.participantDetails.ppsAnswers}
          profileUrl={url}
          context={context}
          participantId={data.participantId}
        />
      </li>
    );
  } catch (e) {
    const errorMessage = 'Error creating participant html';
    logger.logWarn(errorMessage, e, baseApiUrl);
    return null;
  }
};

ParticipantItem.propTypes = {
  data: PropTypes.object.isRequired,
  companyDetailUrl: PropTypes.string.isRequired,
  navigationUrl: PropTypes.string.isRequired,
  key: PropTypes.number.isRequired,
  dtmTag: PropTypes.string.isRequired,
  eventEditionId: PropTypes.string.isRequired,
  baseApiUrl: PropTypes.string.isRequired,
  showPlan: PropTypes.object.isRequired,
  showPlanningEnabled: PropTypes.bool.isRequired,
  digitalPortfolioId: PropTypes.string,
  digitalPortfolioName: PropTypes.string,
  updateFolloweeList: PropTypes.func,
  networkFollowees: PropTypes.arrayOf(PropTypes.object),
  isNetworkParticipant: PropTypes.bool,
  loggedInUserId: PropTypes.string
}.isRequired;

export default ParticipantItem;
